import React, { ReactNode } from 'react'

import Box from 'components_new/atoms/Box'
import Stack from 'components_new/atoms/Stack'
import Text from 'components_new/atoms/Text'

import { useTranslation } from 'language/translator'

interface ErrorPlaceholderProps {
  actions?: ReactNode
  body?: string
  code: 401 | 404 | 500
  sx?: object
}

const TITLE_KEYS = {
  401: 'no_access',
  404: 'not_found',
  500: 'something_went_wrong'
} as const

const BODY_KEYS = {
  401: 'error_401_body',
  404: 'error_404_body',
  500: 'error_500_body'
} as const

/**
 * The ErrorPlaceholder component is used
 * to show an error code together with
 * information when something has gone wrong.
 *
 * It expands, and fills up, all available
 * space.
 */

const ErrorPlaceholder = React.forwardRef(
  (props: ErrorPlaceholderProps, ref: any) => {
    const { actions, body, code, sx } = props
    const { t } = useTranslation()

    return (
      <Box
        ref={ref}
        role="alert"
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          ...sx
        }}
      >
        <Stack alignItems="center" direction="column" sx={{ gap: 2 }}>
          <Text color="primary" variant="h2">
            {code}
          </Text>
          <Text color="text.disabled" variant="h5">
            {t('global', TITLE_KEYS[code])}
          </Text>
          <Text color="text.disabled" variant="body1">
            {body ?? t('global', BODY_KEYS[code])}
          </Text>
          {actions ? (
            <Stack alignItems="center" sx={{ p: 1, gap: 1 }}>
              {actions}
            </Stack>
          ) : null}
        </Stack>
      </Box>
    )
  }
)

ErrorPlaceholder.displayName = 'ErrorPlaceholder'
export default ErrorPlaceholder
